/* ===========================================================================
   ROCKSOLAR system documents — single-line wiring diagram and commissioning
   record, drawn from the finder's own data layer.
   ---------------------------------------------------------------------------
   assets/rs-finder-docs.js

   Loaded by sections/finder-docs.liquid, which renders snippets/finder-data
   on the page first. This file reads that JSON, finds the system named in the
   URL, and draws the requested document:

     /pages/system-documents?p=<product handle>&doc=wiring
     /pages/system-documents?p=<product handle>&doc=commissioning

   With no ?p= it lists every system that has documents.

   THESE ARE REFERENCE DOCUMENTS, NOT CONSTRUCTION DRAWINGS. The single-line
   diagram shows topology and the ratings the store publishes for the kit —
   array size, inverter rating, battery capacity and bus voltage. It does not
   size conductors, overcurrent devices or strings, because that needs the
   panel datasheet, the run lengths and the local code, and it is the licensed
   electrician's job. Every page says so, in print as well as on screen.

   Nothing here names a product. Add a system with its metafields filled and
   its documents exist on the next page load.
   =========================================================================== */
(function () {
  "use strict";

  var host = document.getElementById("rs-docs");
  if (!host) { return; }

  /* ---- data ------------------------------------------------------------- */
  var data;
  try {
    var el = document.getElementById("finder-data");
    data = el ? JSON.parse(el.textContent) : null;
  } catch (e) { data = null; }
  if (!data || !data.systems) {
    host.innerHTML = '<p class="docs-err">The document data could not be loaded. Please try again, or contact us.</p>';
    return;
  }

  var q = {};
  String(location.search || "").replace(/^\?/, "").split("&").forEach(function (kv) {
    if (!kv) { return; }
    var i = kv.indexOf("="), k = i < 0 ? kv : kv.slice(0, i), v = i < 0 ? "" : kv.slice(i + 1);
    try { q[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, " ")); } catch (e2) {}
  });

  var LINKS = window.RS_LINKS || {};
  var SUPPORT = LINKS.support || "/pages/contact-us";
  var FINDER  = LINKS.finder  || "/pages/finder";

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  /* Fixed decimals, then the trailing zeros dropped — 4.40 reads as 4.4 and
     6.00 as 6, but 100 stays 100. */
  function num(n, d) {
    if (n == null || isNaN(n)) { return null; }
    var s = Number(n).toFixed(d == null ? 1 : d);
    return s.indexOf(".") > -1 ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
  }
  function today() {
    var d = new Date(), m = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    return d.getDate() + " " + m[d.getMonth()] + " " + d.getFullYear();
  }

  /* A system has documents when the store has published enough to draw one:
     an inverter rating and an array. Battery-only systems are not solar kits
     and get no diagram — the finder does not recommend them either. */
  function hasDocs(s) { return s.kw != null && s.pvKw != null; }

  /* What the AC side is, in words, from the split-phase flag alone. */
  function acLabel(s) { return s.split ? "120/240 V, 1Ø 3-wire, 60 Hz" : "120 V, 1Ø 2-wire, 60 Hz"; }
  function famLabel(s) {
    return s.family === "gridtie" ? "Grid-tie, no battery"
         : s.family === "hybrid"  ? "Hybrid — grid-interactive with storage"
         : "Off-grid";
  }
  function busLabel(s) {
    if (s.busV == null) { return "battery bus voltage per datasheet"; }
    return s.busV + " V DC nominal bus";
  }

  /* ---- the banner every document carries -------------------------------- */
  function notice() {
    return '<div class="docs-notice" role="note">' +
      '<strong>For customer reference only.</strong> This is not a construction drawing and not a substitute for a design. ' +
      'The installation must be designed, carried out and inspected by a <strong>licensed electrician or professional engineer</strong> ' +
      'to the National Electrical Code and your local code, with the permits and utility approvals your jurisdiction requires. ' +
      'Conductor sizes, overcurrent protection, string configuration and grounding are theirs to determine from the component datasheets and the site — they are deliberately not stated here.' +
    '</div>';
  }

  function docHead(s, title, sub) {
    return '<header class="docs-top">' +
      '<div class="docs-brand"><span class="eyebrow">ROCKSOLAR · System documents</span>' +
        '<h1>' + esc(title) + '</h1>' +
        '<p class="docs-sub">' + esc(sub) + '</p></div>' +
      '<div class="docs-meta">' +
        '<div><span class="k">System</span><span class="v">' + esc(s.title) + '</span></div>' +
        '<div><span class="k">Type</span><span class="v">' + esc(famLabel(s)) + '</span></div>' +
        '<div><span class="k">Generated</span><span class="v">' + esc(today()) + '</span></div>' +
      '</div>' +
    '</header>';
  }

  function docFoot(s, other) {
    return '<footer class="docs-foot">' +
      '<p>Generated from the published specification of <a href="' + esc(s.url) + '">' + esc(s.title) + '</a> on ' + esc(today()) + '. ' +
         'If the product changes, this document changes with it — print the date.</p>' +
      '<p class="docs-actions no-print">' +
        '<button type="button" class="btn btn-amber" data-print="1">Print or save as PDF</button> ' +
        (other ? '<a class="btn" href="' + esc(other.href) + '">' + esc(other.label) + '</a> ' : '') +
        '<a class="btn" href="' + esc(FINDER) + '">Back to the finder</a>' +
      '</p>' +
      '<p class="docs-legal">Not a design. Not a permit drawing. ROCKSOLAR supplies equipment; it does not design or install systems. ' +
         '<a href="' + esc(SUPPORT) + '">Talk to us</a> if anything here does not match what you bought.</p>' +
    '</footer>';
  }

  /* =======================================================================
     SINGLE-LINE DIAGRAM
     A small SVG vocabulary — boxes, wires, labels — and three layouts, one
     per family. Positions are literal because a single-line diagram is read
     left to right and the reader expects the grid on the right, so a layout
     engine would only put things in the wrong place more cleverly.
     ======================================================================= */
  function box(x, y, w, h, title, lines, cls) {
    var out = '<g class="sl-box ' + (cls || "") + '">' +
      '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="4"/>' +
      '<text class="sl-t" x="' + (x + w / 2) + '" y="' + (y + 22) + '" text-anchor="middle">' + esc(title) + '</text>';
    (lines || []).forEach(function (l, i) {
      out += '<text class="sl-l" x="' + (x + w / 2) + '" y="' + (y + 42 + i * 15) + '" text-anchor="middle">' + esc(l) + '</text>';
    });
    return out + '</g>';
  }
  /* A run between two points with its rating written along it. Horizontal
     runs carry the label above the line, anchored at the start, so it never
     drifts into the box the run leaves; vertical runs carry it alongside. */
  function wire(pts, label, cls) {
    var d = pts.map(function (p, i) { return (i ? "L" : "M") + p[0] + " " + p[1]; }).join(" ");
    var out = '<path class="sl-w ' + (cls || "") + '" d="' + d + '"/>';
    if (label) {
      var a = pts[0], b = pts[pts.length - 1];
      var vertical = Math.abs(b[0] - a[0]) < Math.abs(b[1] - a[1]);
      var lab = Array.isArray(label) ? label : [label];
      out += '<g class="sl-wl">';
      lab.forEach(function (t, i) {
        var x = vertical ? a[0] + 8 : a[0] + 6;
        var y = vertical ? (a[1] + b[1]) / 2 + i * 13 : a[1] - 28 + i * 13;
        out += '<text x="' + x + '" y="' + y + '">' + esc(t) + '</text>';
      });
      out += '</g>';
    }
    return out;
  }
  function ground(x, y) {
    return '<g class="sl-gnd"><path d="M' + x + ' ' + y + 'v14"/><path d="M' + (x - 12) + ' ' + (y + 14) + 'h24"/><path d="M' + (x - 8) + ' ' + (y + 19) + 'h16"/><path d="M' + (x - 4) + ' ' + (y + 24) + 'h8"/></g>';
  }
  function disconnect(x, y, label) {
    /* A disconnect is drawn as a gap with a blade — the symbol says more than a box */
    return '<g class="sl-disc"><circle cx="' + x + '" cy="' + y + '" r="3"/><circle cx="' + (x + 30) + '" cy="' + y + '" r="3"/>' +
      '<path d="M' + (x + 3) + ' ' + y + ' L' + (x + 27) + ' ' + (y - 12) + '"/>' +
      '<text x="' + (x + 15) + '" y="' + (y + 22) + '" text-anchor="middle">' + esc(label) + '</text></g>';
  }

  function pvLabel(s) {
    return [num(s.pvKw, 2) + " kW DC array", "OCPD per datasheet"];
  }
  function acShort(s) { return s.split ? "120/240 V 1Ø 60 Hz" : "120 V 1Ø 60 Hz"; }
  function acRun(s) { return [s.split ? "120/240 V 1Ø" : "120 V 1Ø", num(s.kw, 1) + " kW"]; }
  function battRun(s) { return [num(s.kwh, 2) + " kWh", s.busV != null ? s.busV + " V DC" : "V per datasheet"]; }
  /* The grounding electrode, drawn once per diagram under the inverter and
     explained in the legend rather than in a label that collides with wires. */
  function gec(x, y) {
    return wire([[x, y], [x, y + 40]], null, "sl-gnd-w") + ground(x, y + 40) +
      '<text class="sl-l" x="' + (x + 16) + '" y="' + (y + 52) + '">GEC</text>';
  }

  function layoutOffgrid(s) {
    var g = "";
    g += box(20, 150, 150, 90, "PV array", [num(s.pvKw, 2) + " kW DC", "modules per kit contents"], "sl-pv");
    g += wire([[170, 195], [230, 195]], null);
    g += box(230, 150, 150, 90, "PV combiner", ["string fuses / OCPD", "sized by electrician"]);
    g += wire([[380, 195], [440, 195]], pvLabel(s));
    g += disconnect(440, 195, "PV DC disconnect");
    g += wire([[470, 195], [540, 195]], null);
    g += box(540, 120, 200, 150, "Inverter / charger", [
      num(s.kw, 1) + " kW continuous",
      "MPPT solar input",
      s.busV != null ? "battery: " + s.busV + " V DC" : "battery port per datasheet",
      "AC: " + acShort(s)
    ], "sl-inv");
    g += wire([[680, 270], [680, 330]], battRun(s), "sl-dc");
    g += disconnect(665, 330, "Battery disconnect / fuse");
    g += wire([[680, 333], [680, 370]], null, "sl-dc");
    g += box(580, 370, 200, 90, "Battery bank", [
      num(s.kwh, 2) + " kWh " + (s.battBrand ? "· " + s.battBrand : "LiFePO4"),
      s.busV != null ? s.busV + " V nominal" : "voltage per datasheet",
      "BMS comms to inverter where fitted"
    ], "sl-batt");
    g += gec(560, 270);
    g += wire([[640, 120], [640, 70]], null, "sl-opt");
    g += box(540, 20, 200, 50, "Generator (optional)", ["AC input if the inverter has one"], "sl-opt");
    g += wire([[740, 195], [860, 195]], acRun(s));
    g += disconnect(860, 195, "AC disconnect");
    g += wire([[890, 195], [940, 195]], null);
    g += box(940, 150, 170, 90, "Load centre", ["main breaker and branch", "circuits per electrician"]);
    g += wire([[1110, 195], [1135, 195]], null);
    g += '<text class="sl-l" x="1138" y="199">loads</text>';
    g += '<text class="sl-note" x="20" y="525">GEC — grounding electrode conductor to the electrode system; equipment grounding conductor runs with every circuit. Bonding per the inverter manual and NEC 250.</text>';
    return g;
  }

  function layoutGridtie(s) {
    var g = "";
    g += box(20, 150, 150, 90, "PV array", [num(s.pvKw, 2) + " kW DC", "modules per kit contents"], "sl-pv");
    g += wire([[170, 195], [230, 195]], null);
    g += box(230, 150, 150, 90, "Rapid shutdown", ["module-level devices", "NEC 690.12"]);
    g += wire([[380, 195], [440, 195]], pvLabel(s));
    g += disconnect(440, 195, "PV DC disconnect");
    g += wire([[470, 195], [540, 195]], null);
    g += box(540, 120, 200, 150, "Grid-tie inverter", [
      num(s.kw, 1) + " kW AC",
      "MPPT solar input",
      "no battery",
      "AC: " + acShort(s)
    ], "sl-inv");
    g += gec(640, 270);
    g += wire([[740, 195], [860, 195]], acRun(s));
    g += disconnect(860, 195, "AC disconnect (utility-visible)");
    g += wire([[890, 195], [940, 195]], null);
    g += box(940, 150, 170, 90, "Main panel", ["backfed PV breaker", "NEC 705.12 — by electrician"]);
    g += wire([[1110, 195], [1135, 195]], null);
    g += '<text class="sl-l" x="1138" y="199">loads</text>';
    g += wire([[1025, 240], [1025, 300]], null);
    g += box(940, 300, 170, 60, "Utility meter", ["interconnection per utility"]);
    g += wire([[1025, 360], [1025, 410]], null);
    g += '<text class="sl-l" x="1025" y="428" text-anchor="middle">Grid</text>';
    g += '<text class="sl-note" x="20" y="505">Grid-tie inverters stop exporting when the grid goes down (anti-islanding). This system does not provide backup power.</text>';
    g += '<text class="sl-note" x="20" y="525">GEC — grounding electrode conductor to the electrode system; equipment grounding conductor runs with every circuit. Bonding per NEC 250 and 690.</text>';
    return g;
  }

  function layoutHybrid(s) {
    var g = "";
    g += box(20, 150, 150, 90, "PV array", [num(s.pvKw, 2) + " kW DC", "modules per kit contents"], "sl-pv");
    g += wire([[170, 195], [230, 195]], null);
    g += box(230, 150, 150, 90, "Rapid shutdown", ["module-level devices", "NEC 690.12"]);
    g += wire([[380, 195], [440, 195]], pvLabel(s));
    g += disconnect(440, 195, "PV DC disconnect");
    g += wire([[470, 195], [540, 195]], null);
    g += box(540, 120, 200, 150, "Hybrid inverter", [
      num(s.kw, 1) + " kW AC",
      "MPPT solar input",
      s.busV != null ? "battery: " + s.busV + " V DC" : "battery port per datasheet",
      "AC: " + acShort(s)
    ], "sl-inv");
    g += wire([[680, 270], [680, 330]], battRun(s), "sl-dc");
    g += disconnect(665, 330, "Battery disconnect");
    g += wire([[680, 333], [680, 370]], null, "sl-dc");
    g += box(580, 370, 200, 90, "Battery", [
      num(s.kwh, 2) + " kWh " + (s.battBrand ? "· " + s.battBrand : "LiFePO4"),
      s.busV != null ? s.busV + " V nominal" : "voltage per datasheet",
      "BMS comms to inverter"
    ], "sl-batt");
    g += gec(560, 270);
    g += wire([[740, 195], [860, 195]], acRun(s));
    g += box(860, 150, 130, 90, "Transfer switch", ["backup box (ATS)", "NEC 702 / 705"]);
    g += wire([[990, 170], [1015, 170], [1015, 80], [1040, 80]], null);
    g += box(1040, 50, 130, 60, "Essential loads", ["backed-up panel"]);
    g += wire([[990, 220], [1015, 220], [1015, 300], [1040, 300]], null);
    g += box(1040, 270, 130, 60, "Main panel", ["backfed PV breaker"]);
    g += wire([[1105, 330], [1105, 380]], null);
    g += box(1040, 380, 130, 50, "Utility meter", ["then the grid"]);
    g += '<text class="sl-note" x="20" y="505">Backup is partial-home unless the transfer switch and inverter are rated for the whole service. The essential-loads panel is chosen and wired by the electrician.</text>';
    g += '<text class="sl-note" x="20" y="525">GEC — grounding electrode conductor to the electrode system; equipment grounding conductor runs with every circuit. Bonding per NEC 250, 690 and 705.</text>';
    return g;
  }

  function wiringDoc(s) {
    var draw = s.family === "gridtie" ? layoutGridtie : s.family === "hybrid" ? layoutHybrid : layoutOffgrid;
    var svg = '<svg class="sl" viewBox="0 0 1180 540" role="img" aria-label="Single-line diagram for ' + esc(s.title) + '">' +
      draw(s) + '</svg>';

    var ratings =
      '<table class="docs-table">' +
        '<caption>Published ratings used in this drawing</caption>' +
        '<tr><th>PV array</th><td>' + num(s.pvKw, 2) + ' kW DC (STC)</td></tr>' +
        '<tr><th>Inverter</th><td>' + num(s.kw, 1) + ' kW continuous, ' + esc(acLabel(s)) + '</td></tr>' +
        (s.kwh != null ? '<tr><th>Battery</th><td>' + num(s.kwh, 2) + ' kWh' + (s.battBrand ? ', ' + esc(s.battBrand) : '') + ', ' + esc(busLabel(s)) + '</td></tr>' : '<tr><th>Battery</th><td>none — grid-tie</td></tr>') +
        '<tr><th>Family</th><td>' + esc(famLabel(s)) + '</td></tr>' +
      '</table>';

    var notes =
      '<section class="docs-section"><h2>What this drawing does not decide</h2>' +
      '<ul>' +
        '<li><strong>String configuration.</strong> How many modules in series and how many strings in parallel depends on the module Voc and Isc at your record low temperature, and on the inverter\'s MPPT window. From the module datasheet, per NEC 690.7 and 690.8.</li>' +
        '<li><strong>Conductor sizes and overcurrent protection</strong> on every run shown, from the currents above and the run lengths on site. NEC 690, 705 and 706 as applicable.</li>' +
        '<li><strong>Rapid shutdown and arc-fault protection</strong> where the array is on or in a building. NEC 690.11 and 690.12.</li>' +
        '<li><strong>Grounding and bonding</strong> — electrode system, equipment grounding, and the inverter\'s neutral-ground arrangement in island mode.</li>' +
        '<li><strong>Interconnection</strong> for grid-tied and hybrid systems: the utility\'s application, the backfed breaker rating and position (NEC 705.12), and the labelling the AHJ expects.</li>' +
        '<li><strong>Battery location and ventilation</strong>, and the listing requirements that apply to storage in a dwelling (NEC 706, and UL 9540 where required locally).</li>' +
      '</ul></section>';

    return docHead(s, "Single-line wiring diagram", "Topology and published ratings, for your electrician to quote and design from.") +
      notice() +
      '<figure class="docs-fig">' + svg + '<figcaption>Single-line representation. Not to scale. One line stands for the full conductor set on each run.</figcaption></figure>' +
      ratings + notes +
      docFoot(s, { href: "?p=" + encodeURIComponent(s.handle) + "&doc=commissioning", label: "Commissioning record for this system" });
  }

  /* =======================================================================
     COMMISSIONING RECORD
     A form for the installer to fill on site. Expected values are given only
     where the store publishes the figure they come from; everything else says
     "from datasheet" rather than pretending.
     ======================================================================= */
  function field(label, expected, unit) {
    return '<tr><td class="cr-l">' + esc(label) + '</td>' +
      '<td class="cr-e">' + esc(expected || "") + '</td>' +
      '<td class="cr-m"><span class="cr-blank"></span>' + (unit ? ' <span class="cr-u">' + esc(unit) + '</span>' : '') + '</td>' +
      '<td class="cr-ok"><span class="cr-tick"></span></td></tr>';
  }
  function tick(label, note) {
    return '<tr><td class="cr-l" colspan="2">' + esc(label) + (note ? ' <span class="cr-n">' + esc(note) + '</span>' : '') + '</td>' +
      '<td class="cr-m"></td><td class="cr-ok"><span class="cr-tick"></span></td></tr>';
  }
  function table(title, rows, cols) {
    return '<section class="docs-section cr-block"><h2>' + esc(title) + '</h2>' +
      '<table class="cr">' +
        (cols ? '<thead><tr><th>Item</th><th>Expected</th><th>Measured</th><th>OK</th></tr></thead>' : '') +
        '<tbody>' + rows + '</tbody></table></section>';
  }

  function commissioningDoc(s) {
    var hasBatt = s.kwh != null, grid = s.family !== "offgrid";
    var acLN = "120", acLL = s.split ? "240" : null;
    var busNom = s.busV != null ? s.busV + " V" : "per datasheet";
    /* A resting LiFePO4 pack sits between about 94% and 107% of nominal —
       48 to 55 V on a 51.2 V bus. Charging voltage is higher and is not what
       this row asks for. */
    var busRange = (s.busV != null && s.busV <= 60) ? "≈ " + Math.round(s.busV * 0.94) + "–" + Math.round(s.busV * 1.07) + " V resting, LiFePO4" : "per battery datasheet";

    var head =
      '<section class="docs-section cr-block"><h2>Site and system</h2><table class="cr cr-info"><tbody>' +
        '<tr><td class="cr-l">Site address</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">System</td><td class="cr-m wide">' + esc(s.title) + '</td></tr>' +
        '<tr><td class="cr-l">Inverter serial no.</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
        (hasBatt ? '<tr><td class="cr-l">Battery serial no(s).</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' : '') +
        '<tr><td class="cr-l">Installer / company</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">Licence number and jurisdiction</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">Permit number</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">Date of commissioning</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">Weather / irradiance at test</td><td class="cr-m wide"><span class="cr-blank"></span></td></tr>' +
      '</tbody></table></section>';

    var pre = table("Before energising — visual and mechanical",
      tick("All DC and AC conductors terminated, torqued to manufacturer values, and torque-marked") +
      tick("Polarity of every PV string verified at the combiner and at the inverter input") +
      tick("Grounding electrode conductor and equipment grounding continuous; bonding jumper as designed") +
      tick("PV and AC disconnects installed, accessible, and labelled as the AHJ requires") +
      tick("Rapid shutdown initiator installed and labelled", grid ? "NEC 690.12 where the array is on a building" : "where the array is on a building") +
      tick("Overcurrent protection matches the electrician's design on every run") +
      (hasBatt ? tick("Battery installed per listing: clearances, ventilation, mounting, and cable management") : "") +
      tick("Warning and directory labels fitted: PV system, disconnect locations, backfed breaker, ESS as applicable") +
      tick("Array mounting complete: modules secured, racking torqued, wire management clear of edges"), false);

    var pv = table("PV array — with the array in sun, before connecting to the inverter",
      field("Open-circuit voltage per string, Voc", "modules in series × module Voc, corrected for temperature — from datasheet", "V DC") +
      field("Short-circuit current per string, Isc", "≈ module Isc at the irradiance on the day", "A DC") +
      field("Voc within the inverter's maximum input voltage", "must be below the inverter's rated maximum at record low temperature", "yes / no") +
      field("Insulation resistance to ground, each string", "per test procedure; typically > 1 MΩ", "MΩ") +
      field("Number of strings connected", "per design", "") +
      field("Array nameplate", num(s.pvKw, 2) + " kW DC (STC) as published for this kit", "kW"), true);

    var batt = hasBatt ? table("Battery — before closing the battery disconnect",
      field("Battery bus voltage at rest", busRange, "V DC") +
      field("Polarity at the inverter battery terminals", "correct", "") +
      field("Battery disconnect / fuse rating", "per electrician's design and battery datasheet", "A") +
      field("BMS communication to inverter", "established where the kit uses it; battery brand and protocol selected in inverter", "yes / no / n.a.") +
      field("Capacity nameplate", num(s.kwh, 2) + " kWh as published for this kit", "kWh") +
      field("Nominal bus", busNom, "V"), true) : "";

    var inv = table("Inverter — energised, no load",
      field("DC input voltage at inverter, array connected", "matches string Voc above, under load somewhat lower", "V DC") +
      field("AC output voltage, L1–N", acLN + " V nominal", "V AC") +
      (acLL ? field("AC output voltage, L1–L2", acLL + " V nominal (split-phase)", "V AC") : field("AC output voltage, L2–N", "n.a. — single 120 V output", "V AC")) +
      field("Frequency", "60.0 Hz", "Hz") +
      field("No-load / idle draw", "per inverter datasheet", "W") +
      field("Firmware version and settings profile", "recorded", "") +
      (hasBatt ? field("Charge voltage / current settings match battery datasheet", "set per battery manufacturer, not left at default", "yes / no") : ""), true);

    var func = table("Functional tests — under load",
      field("Load test at roughly 50% of rating", "≈ " + num(s.kw * 0.5, 1) + " kW for 15 min, voltage held", "V AC / kW") +
      field("Load test at roughly 90% of rating", "≈ " + num(s.kw * 0.9, 1) + " kW briefly, no fault", "V AC / kW") +
      (grid ? field("Anti-islanding: open the AC disconnect with the grid live", "inverter stops exporting within the listed time", "s") : "") +
      (s.family === "hybrid" ? field("Transfer test: interrupt the grid", "essential loads carried by battery; transfer time recorded", "ms / s") : "") +
      (hasBatt ? field("Charge from array observed", "battery current positive, SOC rising", "A") : "") +
      (hasBatt ? field("Discharge to load observed", "inverter carries load from battery with array covered", "A") : "") +
      (s.family === "offgrid" ? field("Generator start / AC-in transfer, if fitted", "charger accepts generator input; transfer clean", "") : "") +
      field("Monitoring / app connected", "data visible; alarms clear", "yes / no") +
      field("Thermal check after load test", "no hot terminals by touch or IR", "°C"), true);

    var sign =
      '<section class="docs-section cr-block"><h2>Sign-off</h2>' +
      '<p class="cr-decl">I confirm that this system was installed and commissioned in accordance with the design, the manufacturers\' instructions, the National Electrical Code and the local code, and that the values recorded above were measured on site.</p>' +
      '<table class="cr cr-sign"><tbody>' +
        '<tr><td class="cr-l">Installer signature</td><td class="cr-m wide"><span class="cr-blank tall"></span></td><td class="cr-l">Date</td><td class="cr-m"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">Licence no.</td><td class="cr-m wide"><span class="cr-blank"></span></td><td class="cr-l"></td><td class="cr-m"></td></tr>' +
        '<tr><td class="cr-l">Customer signature</td><td class="cr-m wide"><span class="cr-blank tall"></span></td><td class="cr-l">Date</td><td class="cr-m"><span class="cr-blank"></span></td></tr>' +
        '<tr><td class="cr-l">Inspection (AHJ) — permit closed</td><td class="cr-m wide"><span class="cr-blank"></span></td><td class="cr-l">Date</td><td class="cr-m"><span class="cr-blank"></span></td></tr>' +
      '</tbody></table></section>';

    return docHead(s, "Commissioning record", "Printed, measured, ticked and signed on site. Keep it with the permit.") +
      notice() + head + pre + pv + batt + inv + func + sign +
      docFoot(s, { href: "?p=" + encodeURIComponent(s.handle) + "&doc=wiring", label: "Wiring diagram for this system" });
  }

  /* =======================================================================
     INDEX — no ?p= : every system with documents
     ======================================================================= */
  function indexDoc() {
    var rows = "";
    data.systems.slice().sort(function (a, b) { return (a.kw || 0) - (b.kw || 0); }).forEach(function (s) {
      if (!hasDocs(s)) { return; }
      rows += '<tr><td><a href="' + esc(s.url) + '">' + esc(s.title) + '</a><br><span class="docs-dim">' + esc(famLabel(s)) + ' · ' + num(s.kw, 1) + ' kW · ' + num(s.pvKw, 2) + ' kW array' + (s.kwh != null ? ' · ' + num(s.kwh, 2) + ' kWh' : '') + '</span></td>' +
        '<td><a href="?p=' + encodeURIComponent(s.handle) + '&doc=wiring">Wiring diagram</a></td>' +
        '<td><a href="?p=' + encodeURIComponent(s.handle) + '&doc=commissioning">Commissioning record</a></td></tr>';
    });
    return '<header class="docs-top"><div class="docs-brand"><span class="eyebrow">ROCKSOLAR · System documents</span>' +
      '<h1>Wiring diagrams and commissioning records</h1>' +
      '<p class="docs-sub">One of each for every solar system we sell, drawn from its published specification. Reference documents for you and your electrician — not designs.</p></div></header>' +
      notice() +
      '<table class="docs-table docs-index"><thead><tr><th>System</th><th>Drawing</th><th>Record</th></tr></thead><tbody>' + rows + '</tbody></table>' +
      '<p class="docs-actions no-print"><a class="btn btn-amber" href="' + esc(FINDER) + '">Not sure which system? Use the finder</a></p>';
  }

  /* ---- route ------------------------------------------------------------ */
  var handle = q.p, which = q.doc;
  var sys = null;
  if (handle) {
    for (var i = 0; i < data.systems.length; i++) { if (data.systems[i].handle === handle) { sys = data.systems[i]; break; } }
  }

  if (!handle) {
    host.innerHTML = indexDoc();
  } else if (!sys || !hasDocs(sys)) {
    host.innerHTML = '<header class="docs-top"><div class="docs-brand"><span class="eyebrow">ROCKSOLAR · System documents</span><h1>No document for that system</h1>' +
      '<p class="docs-sub">Either the link is out of date or the system does not publish enough of a specification to draw from yet.</p></div></header>' +
      '<p class="docs-actions no-print"><a class="btn" href="' + location.pathname + '">See every system with documents</a> <a class="btn" href="' + esc(SUPPORT) + '">Talk to us</a></p>';
  } else if (which === "commissioning") {
    host.innerHTML = commissioningDoc(sys);
    document.title = "Commissioning record — " + sys.title;
  } else {
    host.innerHTML = wiringDoc(sys);
    document.title = "Wiring diagram — " + sys.title;
  }

  host.addEventListener("click", function (e) {
    var b = e.target.closest ? e.target.closest("[data-print]") : null;
    if (b) { window.print(); }
  });
}());
